import { useEffect, useState, useRef } from "react";
import { useAuth } from "../components/AuthContext.jsx";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { landingApi } from "../api/landingApi.js";
import { EyeClosed } from "lucide-react";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [phone, setphone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    phone: false,
    password: false,
  });
  const phoneRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    document.body.style.backgroundColor = "#1d2951";
    return () => {
      document.body.style.backgroundColor = "#1d2951";
    };
  }, []);

  useEffect(() => {
    phoneRef.current?.focus();
  }, []);

  const validatePhone = (phoneNumber) => {
    const phoneRegex = /^[0-9]{10,11}$/;
    return phoneRegex.test(phoneNumber.replace(/\D/g, ""));
  };

  const clearPhone = () => {
    setphone("");
    setPhoneError("");
    setFieldErrors((prev) => ({ ...prev, phone: false }));
    phoneRef.current?.focus();
  };

  const clearPassword = () => {
    setPassword("");
    setFieldErrors((prev) => ({ ...prev, password: false }));
    passwordRef.current?.focus();
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 11);
    setphone(value);

    if (phoneError) setPhoneError("");
    if (fieldErrors.phone)
      setFieldErrors((prev) => ({ ...prev, phone: false }));

    if (value && !validatePhone(value)) {
      setPhoneError(t("validPhoneNumberRequired"));
    }
  };

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setPhoneError("");
    setFieldErrors({ phone: false, password: false });

    if (!validatePhone(phone)) {
      setPhoneError(t("validPhoneNumberRequired"));
      setFieldErrors((prev) => ({ ...prev, phone: true }));
      return;
    }

    if (!phone.trim()) {
      setFieldErrors((prev) => ({ ...prev, phone: true }));
      return;
    }

    if (!password.trim()) {
      setFieldErrors((prev) => ({ ...prev, password: true }));
      return;
    }

    try {
      const data = await landingApi.loginMerchant(phone, password);
      console.log("data: ", data);

      if (data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("merchantUser", JSON.stringify(data.merchantUser));

        login({
          token: data.token,
          user: data.user,
          merchantUser: data.merchantUser,
        });
        navigate("/");
      } else {
        setError(t("loginFailedToken"));
        setFieldErrors({ phone: true, password: true });
      }
    } catch (err) {
      console.error(err);
      setError(t("loginFailed") + (err.message || t("invalidPhoneOrPassword")));
      setFieldErrors({ phone: true, password: true });
    }
  };

  return (
    <section className="p-6 max-w-sm bg-white border mx-auto my-20 px-9 rounded-3xl">
      <h1 className="text-3xl newsreader-font font-bold mb-4 text-center">
        Sign in
      </h1>
      <form onSubmit={handleLogin} className="flex flex-col gap-4">
        <div className="relative space-y-2">
          <span className="newsreader-font text-lg">Phone Number</span>
          <input
            type="tel"
            placeholder="Enter your ph no."
            value={phone}
            maxLength={12}
            inputMode="numeric"
            onChange={handlePhoneChange}
            className={`border outline-none p-2 bg-gray-100 rounded w-full ${
              fieldErrors.phone ? "border-red-500 border-2" : "border-gray-300"
            }`}
            ref={phoneRef}
          />
          {phone && (
            <button
              type="button"
              onClick={clearPhone}
              className="absolute shadow-md outline-none rounded-full right-3 top-12 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {(phoneError || fieldErrors.phone) && (
            <p className="text-red-500 text-xs">
              {phoneError || t("phoneNumberRequired")}
            </p>
          )}
        </div>

        <div className="relative space-y-2">
          <span className="newsreader-font text-lg">Password</span>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) {
                setFieldErrors((prev) => ({ ...prev, password: false }));
              }
            }}
            ref={passwordRef}
            className={`border bg-gray-100 outline-none p-2 rounded w-full pr-12 transition-all duration-300 ease-in-out ${
              fieldErrors.password
                ? "border-red-500 border-2"
                : "border-gray-300"
            }`}
          />
          {password && (
            <button
              type="button"
              onClick={clearPassword}
              className="absolute shadow-md outline-none rounded-full right-3 top-12 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          {password && (
            <button
              type="button"
              onClick={togglePassword}
              className="absolute right-10 top-12 -translate-y-1/2 text-gray-500 text-sm hover:text-gray-700 transition-all duration-300 ease-in-out"
            >
              {showPassword ? (
                <FontAwesomeIcon
                  icon={faEye}
                  className="transform hover:scale-110 transition-transform duration-200"
                />
              ) : (
                <EyeClosed size={20} />
              )}
            </button>
          )}
          {fieldErrors.password && (
            <p className="text-red-500 text-xs mt-1">{t("passwordRequired")}</p>
          )}
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}

        <div className="flex flex-1 justify-between my-0 items-center">
          <div className="flex gap-1">
            <input
              type="checkbox"
              name=""
              id="rememberMe"
              className="cursor-pointer"
            />
            <button>
              <label
                htmlFor="rememberMe"
                className="hover:text-secondary-color cursor-pointer"
              >
                Remember me
              </label>
            </button>
          </div>
          <button>
            <span className="hover:text-secondary-color">Forgot password</span>
          </button>
        </div>
        <button
          type="submit"
          className="custom-darkblue-button custom-shadow-effect"
        >
          Sign in
        </button>
      </form>
    </section>
  );
};

export default Login;
